import { useCallback, useState } from 'react';
import { useApolloErrorSnackbar } from 'utils/hooks/useApolloErrorSnackbar';
import { GraphQLError, GraphQLFormattedError } from 'graphql';
import { ApolloError, QueryLazyOptions, TypedDocumentNode, useApolloClient } from '@apollo/client';
import { FetchResult } from '@apollo/client/link/core';
import { QueryOptions } from '@apollo/client/core/watchQueryOptions';
import { FETCH_POLICY } from 'common/apollo/FETCH_POLICY';
import { safeJSON } from '@utmost.co/utmost-design-system';
import { useEffectOnce } from '@beeline.com/bp-design-system';
import { useTrackUnusedFields } from 'utils/gql/trackUnusedFields/useTrackUnusedFields';

export { FETCH_POLICY };

export interface UseTypedQueryHookOutput<TData, TVariables> {
  loading: boolean;
  loadingRefetch: boolean;
  data?: TData;
  setData: (data: TData) => void;
  error?: QueryError;
  get: (options?: QueryLazyOptions<TVariables>) => Promise<FetchResult<TData>>;
  refetch: () => Promise<FetchResult<TData>>;
}

export type QueryError = ApolloError | GraphQLError | GraphQLFormattedError<Record<string, any>> | undefined;

export type TypedQueryOptions<TData, TVariables> = Pick<
  QueryOptions<TVariables, TData>,
  'fetchPolicy' | 'errorPolicy' | 'variables' | 'context' | 'notifyOnNetworkStatusChange'
> & {
  onError?: (error: QueryError) => void;
  onCompleted?: (data: TData) => void;
  requestOnInit?: boolean | TVariables;
};

// Small util that wrap you typed query and returns all fields that useQuery gives plus getter function that return a Promise with Data.
// Error handling attached.
// Same API you can find in useTypedMutation
export function useTypedQuery<TData extends object, TVariables>(
  query: TypedDocumentNode<TData, TVariables>,
  {
    onError,
    onCompleted,
    requestOnInit,
    fetchPolicy,
    errorPolicy,
    variables,
    notifyOnNetworkStatusChange,
    context
  }: TypedQueryOptions<TData, TVariables> = {}
) {
  const { query: clientQuery } = useApolloClient();
  const trackUnusedFields = useTrackUnusedFields(query);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingRefetch, setLoadingRefetch] = useState<boolean>(false);
  const [error, setError] = useState<QueryError>(undefined);
  const [data, setData] = useState<TData | undefined>(undefined);
  const [lastOptions, setLastOptions] = useState<QueryLazyOptions<TVariables> | undefined>(undefined);
  const showErrorInSnackbar = useApolloErrorSnackbar();
  const [initialVariables] = useState(requestOnInit);
  const variablesString = variables ? safeJSON.toString(variables) : undefined;
  const contextString = context ? safeJSON.toString(context) : undefined;

  const handleError = useCallback(
    (apolloError: QueryError) => {
      setError(apolloError);
      if (apolloError) {
        if (onError) {
          onError(apolloError);
        } else {
          showErrorInSnackbar(apolloError);
        }
      }
    },
    [showErrorInSnackbar, onError]
  );

  const setResult = useCallback(
    (nextData: TData | undefined) => {
      setData(trackUnusedFields(nextData) ?? undefined);
    },
    [trackUnusedFields]
  );

  const request = useCallback(
    async (queryOptions?: QueryLazyOptions<TVariables>) => {
      const nextVariables = variablesString ? safeJSON.fromString(variablesString) : undefined;
      const nextContext = contextString ? safeJSON.fromString(contextString) : undefined;
      const result = await clientQuery({
        query,
        errorPolicy: errorPolicy ?? 'all',
        fetchPolicy: fetchPolicy ?? FETCH_POLICY.NO_CACHE,
        variables: nextVariables,
        context: nextContext,
        notifyOnNetworkStatusChange,
        ...queryOptions
      });

      const errors = result.error || result.errors?.[0];
      handleError(errors);
      setResult(result.data);
      if (!!result.data) {
        onCompleted?.(result.data);
      }
      return result;
    },
    [
      variablesString,
      contextString,
      clientQuery,
      query,
      errorPolicy,
      fetchPolicy,
      notifyOnNetworkStatusChange,
      handleError,
      setResult,
      onCompleted
    ]
  );

  const get = useCallback(
    async (queryOptions?: QueryLazyOptions<TVariables>) => {
      setLoading(true);
      setLastOptions(queryOptions);
      try {
        return await request(queryOptions);
      } catch (e) {
        handleError(e as QueryError);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [handleError, request]
  );

  useEffectOnce(() => {
    if (initialVariables) {
      get({ variables: typeof initialVariables === 'boolean' ? undefined : initialVariables });
    }
  });

  const refetch = useCallback(async () => {
    setLoadingRefetch(true);
    try {
      return await request(lastOptions);
    } catch (e) {
      handleError(e as QueryError);
      throw e;
    } finally {
      setLoadingRefetch(false);
    }
  }, [handleError, lastOptions, request]);

  return {
    loading,
    loadingRefetch,
    data,
    setData: setResult,
    error,
    get,
    refetch
  } as UseTypedQueryHookOutput<TData, TVariables>;
}
